/**
 * LoginHistoryGrid - Display user's login audit history
 *
 * Implements VULN-1776 requirement for in-app login audit UI.
 * Shows authenticated user's login attempts with timestamps, IP addresses, and success status.
 */

import React, { useMemo } from "react";
import styled from "styled-components";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
  themeQuartz,
} from "ag-grid-community";
import type { ColDef, ValueFormatterParams } from "ag-grid-community";
import { LoginHistory } from "../generated/graphql";

// Register AG Grid v34 modules (required in v34+)
ModuleRegistry.registerModules([AllCommunityModule]);

// Configure AG Grid v34 theme using new Theming API
const gridTheme = themeQuartz;

/**
 * Grid wrapper with fixed height for consistent display
 */
const GridWrapper = styled.div`
  width: 100%;
  height: 400px;
`;

/**
 * Status badge for success/failure indication
 */
const StatusBadge = styled.span<{ success: boolean }>`
  padding: 3px 8px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background-color: ${(props) => (props.success ? "#28a745" : "#dc3545")};
`;

/**
 * Section title
 */
const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #2c3e50;
`;

/**
 * Description text
 */
const Description = styled.p`
  font-size: 14px;
  color: #6c757d;
  margin: 0 0 16px 0;
`;

interface LoginHistoryGridProps {
  loginHistory: LoginHistory[];
}

/**
 * LoginHistoryGrid Component
 *
 * Displays user's login history in a sortable grid format.
 * Features:
 * - Timestamp with formatted date/time
 * - IP address (if available)
 * - Success/failure status badge
 * - Sortable columns
 * - Auto-sized columns
 */
export const LoginHistoryGrid: React.FC<LoginHistoryGridProps> = ({
  loginHistory,
}) => {
  /**
   * Column definitions for the login history grid
   */
  const columnDefs = useMemo<ColDef<LoginHistory>[]>(
    () => [
      {
        headerName: "Date & Time",
        field: "loginAt",
        flex: 2,
        sortable: true,
        sort: "desc", // Most recent first by default
        valueFormatter: (params: ValueFormatterParams<LoginHistory>) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          return date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          });
        },
      },
      {
        headerName: "IP Address",
        field: "ipAddress",
        flex: 1,
        sortable: true,
        valueFormatter: (params: ValueFormatterParams<LoginHistory>) => {
          return params.value || "N/A";
        },
      },
      {
        headerName: "Status",
        field: "success",
        flex: 1,
        sortable: true,
        cellRenderer: (params: { value: boolean }) => {
          return (
            <StatusBadge success={params.value}>
              {params.value ? "Success" : "Failed"}
            </StatusBadge>
          );
        },
      },
    ],
    [],
  );

  /**
   * Default column configuration
   */
  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      filter: false, // Keep it simple - no filtering needed
    }),
    [],
  );

  return (
    <div>
      <SectionTitle>Login History</SectionTitle>
      <Description>
        Review your recent login attempts. This audit log helps you monitor
        account access and detect unauthorized activity.
      </Description>
      <GridWrapper className="ag-theme-quartz">
        <AgGridReact
          theme={gridTheme}
          rowData={loginHistory}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout="normal"
          animateRows={true}
          suppressCellFocus={true}
        />
      </GridWrapper>
    </div>
  );
};
